import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, Activity, ServerCog, ShieldCheck, Globe, AlertTriangle,
  RefreshCw, X, CircleCheck, CircleAlert, CircleX, KeyRound,
} from "lucide-react";
import { diagnosticsApi, apiErrorMessage } from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const REAL_DOMAINS = ["caterctrl.co.uk", "www.caterctrl.co.uk", "leadctrl.caterctrl.co.uk", "leads.caterctrl.co.uk"];

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-GB") : "—");
const DOT = {
  green: "bg-emerald-500", amber: "bg-amber-500", red: "bg-rose-500",
};
const PILL = {
  green: "bg-emerald-50 text-emerald-700 border-emerald-200",
  amber: "bg-amber-50 text-amber-700 border-amber-200",
  red: "bg-rose-50 text-rose-700 border-rose-200",
};
const HEALTH_ICON = { green: CircleCheck, amber: CircleAlert, red: CircleX };
const WINDOWS = ["1h", "6h", "24h", "7d"];
const EVENT_TYPES = ["", "ENQUIRY", "DEMO_REQUEST", "TRIAL_REQUEST", "PAGE_VIEW", "PRICING_VIEW"];

function Boolean({ ok }) {
  return (
    <span className={`inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-[11px] font-bold ${ok ? PILL.green : PILL.red}`}>
      {ok ? "YES" : "NO"}
    </span>
  );
}

function Card({ title, icon: Icon, children, testid }) {
  return (
    <section data-testid={testid} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <h2 className="mb-3 flex items-center gap-2 font-heading text-sm font-bold text-slate-700">
        {Icon && <Icon className="h-4 w-4 text-teal-600" />} {title}
      </h2>
      {children}
    </section>
  );
}

function KV({ k, v, mono }) {
  return (
    <div className="flex items-start justify-between gap-3 border-b border-slate-100 py-1.5 last:border-0">
      <span className="text-xs text-slate-400">{k}</span>
      <span className={`text-right text-xs font-semibold text-slate-700 ${mono ? "font-mono break-all" : ""}`}>{v ?? "—"}</span>
    </div>
  );
}

export default function AdminDiagnostics() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canSetSecret = ["admin", "super_admin"].includes(user?.role);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState(null);
  const [filters, setFilters] = useState({ domain: "caterctrl.co.uk", event_type: "", window: "24h", handoff: "" });
  const [page, setPage] = useState(1);
  const [detail, setDetail] = useState(null);
  const [secretModal, setSecretModal] = useState(null);
  const [sec1, setSec1] = useState("");
  const [sec2, setSec2] = useState("");
  const [saving, setSaving] = useState(false);

  const loadPage = useCallback(() => {
    setLoading(true);
    diagnosticsApi.page({ window: filters.window })
      .then((r) => setData(r.data))
      .catch((e) => toast.error(apiErrorMessage(e)))
      .finally(() => setLoading(false));
  }, [filters.window]);

  const loadEvents = useCallback(() => {
    const params = { window: filters.window, page, page_size: 25 };
    if (filters.domain) params.domain = filters.domain;
    if (filters.event_type) params.event_type = filters.event_type;
    if (filters.handoff) params.handoff = filters.handoff;
    diagnosticsApi.events(params).then((r) => setEvents(r.data)).catch((e) => toast.error(apiErrorMessage(e)));
  }, [filters, page]);

  useEffect(() => { loadPage(); }, [loadPage]);
  useEffect(() => { loadEvents(); }, [loadEvents]);

  const openDetail = (id) => {
    diagnosticsApi.event(id).then((r) => setDetail(r.data)).catch((e) => toast.error(apiErrorMessage(e)));
  };

  const openSecret = (s) => { setSecretModal({ id: s.id, domain: s.site_domain }); setSec1(""); setSec2(""); };

  const submitSecret = () => {
    setSaving(true);
    diagnosticsApi.setSecret(secretModal.id, { secret: sec1 })
      .then(() => {
        toast.success("Secret updated. Set the SAME value in Emergent Secrets as LEADCTRL_SITE_SECRET, then republish.");
        setSecretModal(null);
        loadPage();
      })
      .catch((e) => toast.error(apiErrorMessage(e)))
      .finally(() => setSaving(false));
  };

  const build = data?.build;
  const cfg = data?.ingest_config;
  const health = data?.health;

  return (
    <div className="mx-auto max-w-4xl px-4 py-5 md:py-8" data-testid="admin-diagnostics">
      <button onClick={() => navigate("/more")} className="mb-3 inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-slate-800">
        <ArrowLeft className="h-4 w-4" /> More
      </button>
      <div className="mb-4 flex items-center justify-between gap-2">
        <div>
          <h1 className="flex items-center gap-2 font-heading text-xl font-extrabold tracking-tight text-slate-900 md:text-2xl">
            <Activity className="h-5 w-5 text-teal-600" /> Web Intent Diagnostics
          </h1>
          <p className="mt-0.5 text-xs text-slate-400">Inbound lead pipeline evidence. No secrets, no raw PII.</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide text-slate-500" data-testid="read-only-badge">Read-only</span>
          <button data-testid="diag-refresh" onClick={() => { loadPage(); loadEvents(); }} className="inline-flex min-h-[40px] items-center gap-1.5 rounded-lg border border-slate-200 px-3 text-sm font-semibold text-slate-600 hover:bg-slate-50">
            <RefreshCw className="h-4 w-4" /> Refresh
          </button>
        </div>
      </div>

      {/* Health strip */}
      {health && (
        <div data-testid="health-strip" className="mb-4 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="mb-3 flex items-center gap-2">
            <span className={`h-3 w-3 rounded-full ${DOT[health.overall]}`} />
            <span className="font-heading text-sm font-bold text-slate-800">Pipeline health: <span className="uppercase">{health.overall}</span></span>
          </div>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {health.items.map((it) => {
              const Icon = HEALTH_ICON[it.status];
              return (
                <div key={it.key} data-testid={`health-${it.key}`} className={`flex items-start gap-2 rounded-xl border px-3 py-2 ${PILL[it.status]}`}>
                  <Icon className="mt-0.5 h-4 w-4 shrink-0" />
                  <div className="min-w-0">
                    <p className="text-xs font-bold">{it.label}</p>
                    <p className="truncate text-[11px] opacity-80">{it.detail}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {loading ? (
        <div className="h-40 animate-pulse rounded-2xl border border-slate-200 bg-white" />
      ) : data ? (
        <div className="space-y-4">
          {/* Build & environment */}
          <Card title="Build & environment" icon={ServerCog} testid="diag-build">
            <div className="flex flex-wrap gap-2 pb-2">
              <span className="inline-flex items-center gap-1.5 text-xs text-slate-500">Handoff present <Boolean ok={build.handoff_present} /></span>
              <span className="inline-flex items-center gap-1.5 text-xs text-slate-500">Ingest invokes handoff <Boolean ok={build.ingest_invokes_handoff} /></span>
            </div>
            <KV k="Commit SHA" v={build.git_sha} mono />
            <KV k="ENV" v={build.env} />
            <KV k="DB name" v={build.db_name} mono />
            <KV k="App start" v={fmt(build.app_start_time)} />
            <KV k="Server time" v={fmt(build.server_time)} />
          </Card>

          {/* Ingest config */}
          <Card title="Website-ingest configuration" icon={ShieldCheck} testid="diag-config">
            <KV k="WEB_INGEST_ENDPOINT" v={cfg.WEB_INGEST_ENDPOINT} mono />
            <KV k="LEADCTRL_SITE_DOMAIN" v={cfg.LEADCTRL_SITE_DOMAIN} mono />
            <KV k="LEADCTRL_SITE_ID" v={cfg.LEADCTRL_SITE_ID} mono />
            <KV k="LEADCTRL_TAG_UPSTREAM" v={cfg.LEADCTRL_TAG_UPSTREAM} mono />
            <KV k="LEADCTRL_EVENT_UPSTREAM" v={cfg.LEADCTRL_EVENT_UPSTREAM} mono />
            <KV k="LEADCTRL_SITE_SECRET" v={<span className={cfg.LEADCTRL_SITE_SECRET === "***SET***" ? "text-emerald-600" : "text-rose-600"}>{cfg.LEADCTRL_SITE_SECRET}</span>} />
          </Card>

          {/* Sites */}
          <Card title="CaterCTRL ingest sites" icon={Globe} testid="diag-sites">
            {data.caterctrl_site_missing && (
              <div className="mb-2 flex items-center gap-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs font-bold text-rose-700" data-testid="caterctrl-missing">
                <AlertTriangle className="h-4 w-4" /> No caterctrl.co.uk ingest site row found.
              </div>
            )}
            {data.test_domains_present?.length > 0 && (
              <div className="mb-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs font-bold text-rose-700" data-testid="test-domains">
                Test/demo domains still present: {data.test_domains_present.join(", ")}
              </div>
            )}
            {data.sites.length === 0 ? <p className="text-xs text-slate-400">No sites registered.</p> : (
              <div className="space-y-2">
                {data.sites.map((s) => (
                  <div key={s.id} data-testid={`diag-site-${s.id}`} className="rounded-xl border border-slate-200 p-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-mono text-xs font-semibold text-slate-800">{s.site_domain}</span>
                      <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-bold text-slate-500">{s.product_code}</span>
                      <span className={`rounded-full border px-2 py-0.5 text-[10px] font-bold ${s.active ? PILL.green : PILL.red}`}>{s.active ? "active" : "inactive"}</span>
                      {s.is_test_domain && <span className="rounded-full border px-2 py-0.5 text-[10px] font-bold text-rose-700 border-rose-200 bg-rose-50">TEST</span>}
                    </div>
                    <div className="mt-1 flex flex-wrap gap-x-4 gap-y-0.5 text-[11px] text-slate-400">
                      <span>id {s.id}</span>
                      <span>secret hash {s.has_hmac_secret_hash ? "present" : "MISSING"}</span>
                      <span>created {fmt(s.created_at)}</span>
                      {s.rotated_at && <span>rotated {fmt(s.rotated_at)}</span>}
                    </div>
                    {canSetSecret && REAL_DOMAINS.includes(s.site_domain) && (
                      <button data-testid={`set-secret-${s.id}`} onClick={() => openSecret(s)}
                        className="mt-2 inline-flex items-center gap-1.5 rounded-lg border border-teal-200 bg-teal-50 px-2.5 py-1 text-xs font-semibold text-teal-700 hover:bg-teal-100">
                        <KeyRound className="h-3.5 w-3.5" /> Set / Rotate Secret
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </Card>

          {/* Errors + audit summary */}
          <Card title={`Ingest errors (${data.errors_summary.window})`} icon={AlertTriangle} testid="diag-errors">
            <p className="text-xs text-slate-500">Total: <b className={data.errors_summary.total ? "text-rose-600" : "text-emerald-600"}>{data.errors_summary.total}</b></p>
            {Object.entries(data.errors_summary.by_class).map(([k, v]) => (
              <KV key={k} k={k} v={v} />
            ))}
            <p className="mt-2 border-t border-slate-100 pt-2 text-[11px] text-slate-400">{data.audit_summary.note} Inbound handoff writes in window: <b>{data.audit_summary.inbound_handoff_audit_events}</b></p>
          </Card>

          {/* Events */}
          <Card title="Recent web events" icon={Activity} testid="diag-events">
            <div className="mb-3 flex flex-wrap gap-2">
              <input data-testid="filter-domain" value={filters.domain} onChange={(e) => { setPage(1); setFilters({ ...filters, domain: e.target.value }); }} placeholder="domain" className="min-h-[36px] rounded-lg border border-slate-200 px-2 text-xs" />
              <select data-testid="filter-type" value={filters.event_type} onChange={(e) => { setPage(1); setFilters({ ...filters, event_type: e.target.value }); }} className="min-h-[36px] rounded-lg border border-slate-200 px-2 text-xs">
                {EVENT_TYPES.map((t) => <option key={t} value={t}>{t || "all types"}</option>)}
              </select>
              <select data-testid="filter-window" value={filters.window} onChange={(e) => { setPage(1); setFilters({ ...filters, window: e.target.value }); }} className="min-h-[36px] rounded-lg border border-slate-200 px-2 text-xs">
                {WINDOWS.map((w) => <option key={w} value={w}>last {w}</option>)}
              </select>
              <select data-testid="filter-handoff" value={filters.handoff} onChange={(e) => { setPage(1); setFilters({ ...filters, handoff: e.target.value }); }} className="min-h-[36px] rounded-lg border border-slate-200 px-2 text-xs">
                <option value="">any handoff</option>
                <option value="done">handoff done</option>
                <option value="missing">handoff missing</option>
              </select>
            </div>
            {!events || events.items.length === 0 ? (
              <p className="py-6 text-center text-xs text-slate-400" data-testid="events-empty">No events in this window.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs" data-testid="events-table">
                  <thead className="border-b border-slate-200 text-slate-400">
                    <tr><th className="py-1.5 pr-2">Time</th><th className="pr-2">Type</th><th className="pr-2">Email</th><th className="pr-2">Company</th><th className="pr-2">Handoff</th><th /></tr>
                  </thead>
                  <tbody>
                    {events.items.map((ev) => (
                      <tr key={ev.id} data-testid={`event-row-${ev.id}`} className="border-b border-slate-100">
                        <td className="py-1.5 pr-2 text-slate-500">{fmt(ev.timestamp)}</td>
                        <td className="pr-2 font-semibold text-slate-700">{ev.event_type}</td>
                        <td className="pr-2 font-mono text-slate-500">{ev.masked_email || "—"}</td>
                        <td className="pr-2 text-slate-600">{ev.company || "—"}</td>
                        <td className="pr-2">
                          <span className={`rounded-full border px-1.5 py-0.5 text-[10px] font-bold ${ev.handoff_status === "done" ? PILL.green : ev.handoff_status === "missing" ? PILL.red : PILL.amber}`}>{ev.handoff_status}</span>
                        </td>
                        <td><button data-testid={`event-open-${ev.id}`} onClick={() => openDetail(ev.id)} className="font-semibold text-teal-700 hover:underline">View</button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="mt-2 flex items-center justify-between text-xs text-slate-400">
                  <span>{events.total} total</span>
                  <div className="flex gap-2">
                    <button disabled={page <= 1} onClick={() => setPage(page - 1)} className="rounded border border-slate-200 px-2 py-1 disabled:opacity-40">Prev</button>
                    <span>Page {events.page}</span>
                    <button disabled={page * events.page_size >= events.total} onClick={() => setPage(page + 1)} className="rounded border border-slate-200 px-2 py-1 disabled:opacity-40">Next</button>
                  </div>
                </div>
              </div>
            )}
          </Card>
        </div>
      ) : null}

      {/* Detail drawer */}
      {detail && (
        <div className="fixed inset-0 z-50 flex justify-end" data-testid="event-drawer">
          <div className="absolute inset-0 bg-slate-900/40" onClick={() => setDetail(null)} />
          <div className="relative z-10 h-full w-full max-w-md overflow-y-auto bg-white p-5 shadow-xl">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="font-heading text-base font-bold text-slate-900">Event detail</h3>
              <button data-testid="drawer-close" onClick={() => setDetail(null)} className="rounded-md p-1 text-slate-400 hover:bg-slate-100"><X className="h-5 w-5" /></button>
            </div>
            <span className="mb-3 inline-block rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[10px] font-bold uppercase text-slate-500">Masked · read-only</span>
            <KV k="Type" v={detail.event_type} />
            <KV k="Time" v={fmt(detail.timestamp)} />
            <KV k="Path" v={detail.source_path} mono />
            <KV k="Product site" v={detail.product_site} mono />
            <h4 className="mt-3 text-xs font-bold text-slate-600">Masked payload</h4>
            <KV k="Email" v={detail.masked_payload.email} mono />
            <KV k="Phone" v={detail.masked_payload.phone} mono />
            <KV k="Company" v={detail.masked_payload.company} />
            <KV k="Postcode" v={detail.masked_payload.postcode} mono />
            <KV k="Name" v={detail.masked_payload.name} />
            <KV k="Message present" v={String(detail.masked_payload.message_present)} />
            <h4 className="mt-3 text-xs font-bold text-slate-600">Handoff</h4>
            <KV k="Status" v={detail.handoff.status} />
            <KV k="Organisation" v={detail.handoff.organisation_id} mono />
            <KV k="Contact" v={detail.handoff.contact_id} mono />
            <KV k="Opportunity" v={detail.handoff.opportunity_id} mono />
            <KV k="At" v={fmt(detail.handoff.at)} />
            <h4 className="mt-3 text-xs font-bold text-slate-600">Idempotency</h4>
            <KV k="Source event id" v={detail.idempotency.source_event_id} mono />
            <KV k="Matched org" v={detail.idempotency.matched_org_id} mono />
            <KV k="Match method" v={detail.idempotency.match_method} />
          </div>
        </div>
      )}

      {/* Set-secret modal */}
      {secretModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="set-secret-modal">
          <div className="absolute inset-0 bg-slate-900/40" onClick={() => setSecretModal(null)} />
          <div className="relative z-10 w-full max-w-md rounded-2xl bg-white p-5 shadow-xl">
            <div className="mb-2 flex items-center justify-between">
              <h3 className="flex items-center gap-2 font-heading text-base font-bold text-slate-900"><KeyRound className="h-4 w-4 text-teal-600" /> Set HMAC Site Secret</h3>
              <button data-testid="set-secret-close" onClick={() => setSecretModal(null)} className="rounded-md p-1 text-slate-400 hover:bg-slate-100"><X className="h-5 w-5" /></button>
            </div>
            <p className="text-xs text-slate-500">For <b className="font-mono">{secretModal.domain}</b></p>
            <div className="mt-3 space-y-2">
              <input data-testid="secret-input-1" type="password" autoComplete="new-password" value={sec1} onChange={(e) => setSec1(e.target.value)} placeholder="Paste HMAC Site Secret" className="min-h-[42px] w-full rounded-lg border border-slate-200 px-3 text-sm focus:border-teal-500 focus:outline-none" />
              <input data-testid="secret-input-2" type="password" autoComplete="new-password" value={sec2} onChange={(e) => setSec2(e.target.value)} placeholder="Re-paste to confirm" className="min-h-[42px] w-full rounded-lg border border-slate-200 px-3 text-sm focus:border-teal-500 focus:outline-none" />
            </div>
            {sec1 && sec2 && sec1 !== sec2 && <p className="mt-1 text-xs font-semibold text-rose-600" data-testid="secret-mismatch">Secrets do not match.</p>}
            <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800" data-testid="secret-warning">
              This value will be hashed and stored. The plaintext will <b>NOT</b> be shown back. Keep a copy in your own password manager.
            </div>
            <div className="mt-2 rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600">
              You must also set the SAME value as the <b>LEADCTRL_SITE_SECRET</b> env var in Emergent Manage Publishes → Secrets so the /lc-collect proxy can sign upstream.
            </div>
            <button data-testid="secret-submit" disabled={!sec1 || sec1 !== sec2 || saving} onClick={submitSecret}
              className="mt-4 inline-flex min-h-[44px] w-full items-center justify-center gap-2 rounded-xl bg-teal-700 px-4 text-sm font-bold text-white hover:bg-teal-800 disabled:opacity-40">
              {saving ? "Saving…" : "Save secret"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
